import React, { useEffect, useState } from 'react';
import { Polygon } from 'react-leaflet';
import { getPortfolioMetricColor, getPortfolioMetricValue } from 'src/app/portfolio/helpers/portfolioHelpers';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { getBufferedPolygon } from 'src/shared/helpers/polygonHelpers';
import { swapCoordinateLatLng } from 'src/shared/helpers/coordinateHelpers';
import { useLazySitePolygonsQuery, useSitePopupQuery } from '../../state/api/portfolioSiteExplorerGraphSlice';

type PortfolioSiteExplorerPolygonProps = {
    siteId?: string;
    selectedMetric?: string;
    speciesColorDomain?: { min: number; max: number };
};

const PortfolioSiteExplorerPolygon = ({ siteId, selectedMetric, speciesColorDomain }: PortfolioSiteExplorerPolygonProps) => {
    const [childrenCoordinates, setChildrenCoordinates] = useState<[number, number][][][]>([]);
    const { currentCustomerId } = useCustomer();
    const [fetchPolygonData] = useLazySitePolygonsQuery();

    const { currentData: siteData } = useSitePopupQuery(
        {
            customerId: currentCustomerId,
            siteId: siteId || '',
        },
        {
            skip: !siteId,
        }
    );

    useEffect(() => {
        if (!siteId) {
            return;
        }
        const loadPolygonData = async () => {
            const polygonData = await fetchPolygonData({
                customerId: currentCustomerId,
                siteId: siteId,
            }).unwrap();

            setChildrenCoordinates(polygonData.flatMap(child => child.coordinates as unknown as [number, number][][][]));
        };
        loadPolygonData();
    }, [siteId]);

    if (!siteData || !childrenCoordinates.length) {
        return null;
    }
    // Get the metric value for color coding — siteData comes from sitePopupQuery which includes
    // species counts directly, so no external speciesCountsBySiteId map is needed.
    let metricValue = 0;
    if (siteId && selectedMetric) {
        metricValue =
            getPortfolioMetricValue({
                siteMetrics: siteData?.metrics,
                metric: selectedMetric,
                redList: siteData?.speciesRedListCount,
                invasive: siteData?.speciesInvasiveCount,
            }) || 0;
    }

    // Get color based on metric value
    const color = getPortfolioMetricColor(metricValue, selectedMetric, speciesColorDomain);

    const bufferedSiteRawCoordinates = getBufferedPolygon({
        coordinates: (childrenCoordinates.flat().flat() as [number, number][]).map(coordinate => swapCoordinateLatLng(coordinate)),
        percentageOfArea: 10,
        units: 'meters',
    });
    const bufferedSiteCoordinates = bufferedSiteRawCoordinates.geometry.coordinates[0].map(coordinate => [
        coordinate[1],
        coordinate[0],
    ]) as [number, number][];

    return (
        <>
            {/* Buffered site */}
            <Polygon
                positions={bufferedSiteCoordinates}
                pathOptions={{
                    color: color,
                    weight: 0,
                    fillColor: color,
                    fillOpacity: 0.15,
                    opacity: 0.15,
                }}
            />
            {/* Children polygons */}
            {childrenCoordinates.map((coordinates, index) => (
                <Polygon
                    key={index}
                    positions={coordinates}
                    pathOptions={{
                        color: color,
                        weight: 2,
                        fillColor: color,
                        fillOpacity: 0.3,
                    }}
                />
            ))}
        </>
    );
};

export default PortfolioSiteExplorerPolygon;
